import {
    Box,
    Typography,
    Container,
    Button,
    Stack,
    Card,
    CardContent,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import EmailIcon from '@mui/icons-material/Email';
import SportsIcon from '@mui/icons-material/Sports';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPath } from './Pages';

export default function Page404(): React.ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleGoHome = (): void => {
        navigate(getPath('home'));
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
            }}
        >
            <Container maxWidth="md">
                <Card
                    elevation={8}
                    sx={{
                        borderRadius: 4,
                        overflow: 'hidden',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                        <Stack spacing={4} alignItems="center">
                            {/* Sports Icon with Animation */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: { xs: 200, md: 280 },
                                        height: { xs: 200, md: 280 },
                                        borderRadius: '50%',
                                        background: (theme) =>
                                            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow:
                                            '0 10px 40px rgba(186, 242, 0, 0.3)',
                                        animation:
                                            'pulse 2s ease-in-out infinite',
                                        '@keyframes pulse': {
                                            '0%': {
                                                transform: 'scale(1)',
                                                boxShadow:
                                                    '0 10px 40px rgba(186, 242, 0, 0.3)',
                                            },
                                            '50%': {
                                                transform: 'scale(1.05)',
                                                boxShadow:
                                                    '0 15px 50px rgba(186, 242, 0, 0.4)',
                                            },
                                            '100%': {
                                                transform: 'scale(1)',
                                                boxShadow:
                                                    '0 10px 40px rgba(186, 242, 0, 0.3)',
                                            },
                                        },
                                    }}
                                >
                                    <SportsIcon
                                        sx={{
                                            fontSize: { xs: 120, md: 160 },
                                            color: 'primary.contrastText',
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* 404 Title */}
                            <Typography
                                variant="h1"
                                sx={{
                                    fontSize: { xs: '5rem', md: '8rem' },
                                    fontWeight: 800,
                                    color: 'var(--brand-text)',
                                    textShadow:
                                        '2px 2px 4px rgba(0, 0, 0, 0.1)',
                                    lineHeight: 1,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                404
                            </Typography>

                            {/* Main Message */}
                            <Stack
                                spacing={2}
                                alignItems="center"
                                textAlign="center"
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#333',
                                        fontSize: { xs: '1.5rem', md: '2rem' },
                                    }}
                                >
                                    {t('page404.title')}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: '#666',
                                        fontSize: {
                                            xs: '1rem',
                                            md: '1.125rem',
                                        },
                                        maxWidth: '600px',
                                        px: 2,
                                    }}
                                >
                                    {t('page404.description')}
                                </Typography>
                            </Stack>

                            {/* Action Buttons */}
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                sx={{ width: '100%', maxWidth: '500px', mt: 2 }}
                            >
                                <Button
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    startIcon={<HomeIcon />}
                                    onClick={handleGoHome}
                                    sx={{
                                        backgroundColor: 'primary.main',
                                        color: 'primary.contrastText',
                                        py: 1.5,
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        boxShadow:
                                            '0 4px 12px rgba(186, 242, 0, 0.3)',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                            boxShadow:
                                                '0 6px 16px rgba(186, 242, 0, 0.4)',
                                            transform: 'translateY(-2px)',
                                        },
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    {t('page404.backHome')}
                                </Button>
                            </Stack>

                            {/* Contact Section */}
                            <Card
                                variant="outlined"
                                sx={{
                                    width: '100%',
                                    maxWidth: '600px',
                                    mt: 4,
                                    borderRadius: 3,
                                    borderColor: '#e0e0e0',
                                    backgroundColor: '#f9fafb',
                                }}
                            >
                                <CardContent>
                                    <Stack
                                        spacing={2}
                                        alignItems="center"
                                        textAlign="center"
                                    >
                                        <EmailIcon
                                            sx={{
                                                fontSize: 40,
                                                color: 'var(--brand-text)',
                                            }}
                                        />
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#333',
                                            }}
                                        >
                                            {t('page404.contactTitle')}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#666',
                                                mb: 1,
                                            }}
                                        >
                                            {t('page404.contactDescription')}
                                        </Typography>
                                        <Box
                                            component="a"
                                            href="mailto:info@sporttia.com"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                color: 'var(--brand-text)',
                                                textDecoration: 'none',
                                                fontWeight: 600,
                                                fontSize: '1.125rem',
                                                '&:hover': {
                                                    textDecoration: 'underline',
                                                },
                                            }}
                                        >
                                            <EmailIcon fontSize="small" />
                                            info@sporttia.com
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>

                            {/* Decorative Sports Images */}
                            <Stack
                                direction="row"
                                spacing={2}
                                sx={{
                                    mt: 3,
                                    opacity: 0.6,
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                    gap: 2,
                                }}
                            >
                                {[1, 2, 3, 4, 5].map((num) => (
                                    <Box
                                        key={num}
                                        component="img"
                                        src={`/sports/${num}.png`}
                                        alt={`Sport ${num}`}
                                        sx={{
                                            width: { xs: 40, md: 50 },
                                            height: { xs: 40, md: 50 },
                                            objectFit: 'contain',
                                            filter: 'grayscale(50%)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                filter: 'grayscale(0%)',
                                                transform: 'scale(1.2)',
                                            },
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
}
